import React, { useEffect } from 'react';
import Icon, { IconProps } from '../../foundations/Icons';
import { Sentry } from '../../utilities';

/**
 * Component that renders a Toast message
 */

export type ToastProps = {
  notificationType: 'success' | 'error' | 'info';
  notificationTitle?: string;
  content: string;
  handleHide: () => void;
};

function getStyle(
  notificationType: ToastProps['notificationType'] | 'default'
): { icon: IconProps['name']; iconColor: string; bgColor: string } {
  switch (notificationType) {
    case 'success': {
      return {
        icon: 'lInputsSuccess',
        iconColor: 'text-success-foreground',
        bgColor: 'bg-success-background',
      };
    }
    case 'error': {
      return {
        icon: 'lInputsError',
        iconColor: 'text-error-foreground',
        bgColor: 'bg-error-background',
      };
    }
    default: {
      return {
        icon: 'lInputsInfo',
        iconColor: 'text-warning-foreground',
        bgColor: 'bg-warning-background',
      };
    }
  }
}

export const Toast = (props: ToastProps) => {
  const {
    notificationType = 'default',
    notificationTitle,
    content,
    handleHide,
  } = props;

  const cssStyle = getStyle(notificationType);

  useEffect(() => {
    if (!['success', 'error', 'info'].includes(notificationType)) {
      Sentry.captureException(
        `Trying to render toast with ${notificationType}`
      );
    }
  }, [notificationType]);
  return (
    <div
      data-testid="toast-notification"
      className={`mb-xs w-tile cursor-pointer p-s ${cssStyle.bgColor}`}
      onClick={handleHide}
      role="button"
      tabIndex={0}
      onKeyDown={handleHide}
    >
      <div className="flex gap-xs">
        <div className={`size-6 ${cssStyle.iconColor}`}>
          <Icon name={cssStyle.icon} inheritFill />
        </div>
        <div className="flex grow flex-col gap-xxxs">
          {notificationTitle && (
            <div className="font-body text-label-small text-regular">
              {notificationTitle}
            </div>
          )}
          <div className="font-body text-label-small text-regular">
            {content}
          </div>
        </div>
        <div className="size-5 text-regular">
          <Icon name="mInputsClose" inheritFill />
        </div>
      </div>
    </div>
  );
};

export default Toast;
